<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <graph-tool-bar granularity="week" @on-selected="onPanelSelect"></graph-tool-bar>
    </div>

    <div class="md-layout-item md-xsmall-size-100 md-small-size-100 md-size-50">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue"></md-card-header>

        <md-card-content>
          <highcharts class="charts" :options="columnChart" ref="columnChart"></highcharts>
        </md-card-content>
      </md-card>
    </div>
    <div class="md-layout-item md-xsmall-size-100 md-small-size-100 md-size-50">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue"></md-card-header>

        <md-card-content>
          <highcharts class="charts" :options="pieChartOptions" ref="pieChart"></highcharts>
        </md-card-content>
      </md-card>
    </div>

    <div class="md-layout-item md-xsmall-size-100 md-small-size-100 md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue"></md-card-header>

        <md-card-content>
          <highcharts class="chart-column" :options="lineChartOptions" ref="lineChart"></highcharts>
        </md-card-content>
      </md-card>
    </div>

    <snack-bar ref="snackbar"></snack-bar>
  </div>
</template>


<script>
import graphToolBar from "@/components/GraphToolBar";
import columnChartOptions from "@/config/charts/column";
import areaChartOptions from "@/config/charts/area";
import axios from "axios";
import { getSelectedDeviceToken } from "@/storage/memoryStorage";
import snackBar from "@/components/SnackBar";
import { startOfDay, format } from "date-fns";

export default {
  components: {
    "graph-tool-bar": graphToolBar,
    "snack-bar": snackBar
  },

  data() {
    return {
      pieChartOptions: null,
      columnChart: null,
      lineChartOptions: null,
      selectedToolBar: null
    };
  },

  created() {
    const redrawPieChart = fromTs => {
      const fromUnixTS = fromTs / 1000;
      const toUnixTS = fromUnixTS + 86399;
      this.getPieChartData(
        this.selectedToolBar.device.mac,
        fromUnixTS,
        toUnixTS
      );
      this.getPieChartDrillDownData(
        this.selectedToolBar.device.mac,
        fromUnixTS,
        toUnixTS
      );
    };

    this.columnChart = {
      time: {
        useUTC: false
      },
      chart: {
        type: "column"
      },
      title: {
        text: this.$lang.charts.percentage_availability
      },
      xAxis: {
        type: "datetime"
      },
      yAxis: {
        min: 0,
        title: {
          text: ""
        },
        labels: {
          format: "{value} %"
        },
        type: "linear"
      },

      tooltip: {
        shared: true,
        valueDecimals: 0,
        dateTimeLabelFormats: {
          second: "%e.%m.%Y  [%H:%M]",
          minute: "%e.%m.%Y  [%H:%M]",
          millisecond: "%e.%m.%Y  [%H:%M]"
        },
        style: {
          whiteSpace: "nowrap",
          fontSize: "14px"
        },
        pointFormat:
          "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y}s</b> ({point.percentage:.2f}%)<br/>",
        headerFormat: "<span style='font-size: 12px'>{point.key}</span><br/>",
        animation: true
      },
      plotOptions: {
        column: {
          stacking: "percent",
          cursor: "pointer"
        }
      },
      legend: {
        verticalAlign: "top"
      },
      credits: {
        enabled: false
      },
      series: [
        {
          name: "Online",
          data: [],
          color: "#4caf50"
        },
        {
          name: "Offline",
          data: [],
          color: "#ff5722"
        }
      ]
    };

    this.columnChart.chart.events = {
      redraw: function(event) {
        this.hideLoading();
      }
    };

    this.columnChart.plotOptions.column.events = {
      click: function(event) {
        redrawPieChart(event.point.x);
      }
    };

    this.pieChartOptions = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        type: "pie"
      },
      title: {
        text: ""
      },
      tooltip: {
        dateTimeLabelFormats: {
          second: "%e.%m.%Y  %H:%M:%S"
        },
        formatter: function() {
          return (
            format(this.point.name, "HH:mm D/M/YYYY ") +
            "<br><b>" +
            this.point.status +
            "</b><br><b>Uptime:</b> " +
            this.point.uptime +
            "%"
          );
        }
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            formatter: function() {
              return format(this.point.name, "HH:mm");
            }
          },
          showInLegend: false
        }
      },
      credits: {
        enabled: false
      },
      series: [
        {
          colorByPoint: false,
          data: []
        }
      ],
      drilldown: {
        series: []
      }
    };

    this.pieChartOptions.chart.events = {
      redraw: function(event) {
        this.hideLoading();
      }
    };

    this.lineChartOptions = JSON.parse(JSON.stringify(areaChartOptions));
    this.lineChartOptions.title.text = this.$lang.charts.uptime;
    this.lineChartOptions.yAxis.labels.format = "{value} %";
    this.lineChartOptions.tooltip.pointFormat =
      "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} %</b><br/>";

    this.lineChartOptions.series = [
      {
        name: "Uptime",
        data: [],
        type: "area"
      }
    ];
    this.lineChartOptions.yAxis.type = "logarithmic";
    this.lineChartOptions.legend.enabled = false;

    this.lineChartOptions.chart.events = {
      redraw: function(event) {
        this.hideLoading();
      }
    };
  },

  methods: {
    onPanelSelect(selected) {
      this.selectedToolBar = selected;
      const todayFrom = format(
        startOfDay(new Date(selected.date.to * 1000)),
        "X"
      );

      this.$nextTick(() => {
        this.getColumnChartData(selected);
        this.getPieChartData(selected.device.mac, todayFrom, selected.date.to);
        this.getPieChartDrillDownData(
          selected.device.mac,
          todayFrom,
          selected.date.to
        );
        this.getLineChartData(selected);
      });
    },

    getPieChartData(deviceMAC, from, to) {
      const { chart } = this.$refs.pieChart;
      chart.showLoading();

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/tools/monitoring",
        data: {
          token: getSelectedDeviceToken(deviceMAC),
          from: from,
          to: to,
          period: "30MINUTE"
        },
        withCredentials: true
      })
        .then(response => {
          let chartData = [];
          const responseLength = response.data.data.length;

          for (let index = 0; index < responseLength; index++) {
            chartData.push({
              name: response.data.data[index].timestamp * 1000,
              y: 1,
              drilldown: response.data.data[index].timestamp,
              color:
                response.data.data[index].offline == 0 ? "#4caf50" : "#ff5722",
              status:
                response.data.data[index].offline == 0 ? "ONLINE" : "OFFLINE",
              uptime: response.data.data[index].uptime
            });
          }

          chart.update(
            {
              title: {
                text:
                  this.$lang.charts.monitoring_availability +
                  " (" +
                  format(new Date(from * 1000), "D.M.YYYY") +
                  ")"
              }
            },
            false,
            false,
            false
          );
          chart.series[0].setData(chartData);
        })
        .catch(error => {
          if (error.response.data.code == 610) {
            this.$refs.snackbar.notify(this.$lang.messages.data_not_found);
            chart.series[0].setData([]);
          } else {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getPieChartDrillDownData(deviceMAC, from, to) {
      const dateChunks = this.getDateChunks(from, to);
      const dateChunksLength = dateChunks.length;

      const { chart } = this.$refs.pieChart;
      chart.showLoading();

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/tools/monitoring",
        data: {
          token: getSelectedDeviceToken(deviceMAC),
          from: from,
          to: to,
          period: "MINUTE"
        },
        withCredentials: true
      })
        .then(response => {
          if (response.data.status === "OK") {
            let drillDownSeries = [];

            for (
              let dateRangeIndex = 1;
              dateRangeIndex < dateChunksLength;
              dateRangeIndex++
            ) {
              const fromTS = dateChunks[dateRangeIndex - 1];
              const toTS = dateChunks[dateRangeIndex];

              const statusChunkData = response.data.data.filter(
                el => el.timestamp >= fromTS && el.timestamp < toTS
              );

              let oneSerie = {
                name: fromTS * 1000,
                id: fromTS,
                data: []
              };

              const statusChunkDataLength = statusChunkData.length;

              for (let index = 0; index < statusChunkDataLength; index++) {
                oneSerie.data.push({
                  name: statusChunkData[index].timestamp * 1000,
                  y: 1,
                  color:
                    statusChunkData[index].offline == 0 ? "#4caf50" : "#ff5722",
                  status:
                    statusChunkData[index].offline == 0 ? "ONLINE" : "OFFLINE",
                  uptime: statusChunkData[index].uptime
                });
              }

              drillDownSeries.push(oneSerie);
            }

            chart.update({
              drilldown: {
                series: drillDownSeries
              }
            });
          } else {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        })
        .catch(error => {
          if (error.response.data.code == 610) {
            this.$refs.snackbar.notify(this.$lang.messages.data_not_found);
            chart.series[0].setData([]);
          } else {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getColumnChartData(selected) {
      const { chart } = this.$refs.columnChart;
      chart.showLoading();

      //apiTo = parseInt(apiTo) + 1;

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/tools/monitoring",
        data: {
          token: getSelectedDeviceToken(selected.device.mac),
          from: selected.date.from,
          to: selected.date.to,
          period: "DAY",
          format: "H"
        },
        withCredentials: true
      })
        .then(response => {
          chart.series[0].setData(
            response.data.data.online,
            false,
            false,
            false
          );
          chart.series[1].setData(
            response.data.data.offline,
            false,
            false,
            false
          );
          chart.xAxis[0].setExtremes(null, null, true);
        })
        .catch(error => {
          if (error.response.data.code === 610) {
            this.$refs.snackbar.notify(this.$lang.messages.data_not_found);
            chart.series[0].setData([]);
            chart.series[1].setData([]);
          } else {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getLineChartData(selected) {
      const { chart } = this.$refs.lineChart;
      chart.showLoading();

      //apiTo = parseInt(apiTo) + 1;

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/tools/monitoring",
        data: {
          token: getSelectedDeviceToken(selected.device.mac),
          from: selected.date.from,
          to: selected.date.to,
          period: "DAY",
          format: "H"
        },
        withCredentials: true
      })
        .then(response => {
          chart.series[0].setData(
            response.data.data.uptime,
            false,
            false,
            false
          );
          chart.xAxis[0].setExtremes(null, null, true);
        })
        .catch(error => {
          if (error.response.data.code === 610) {
            this.$refs.snackbar.notify(this.$lang.messages.data_not_found);
            chart.series[0].setData([]);
          } else {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getDateChunks(startUnixTS, endUnixTS) {
      let start = new Date(startUnixTS * 1000);
      let end = new Date(endUnixTS * 1000);
      var arr = new Array();
      var dt = new Date(start);

      while (dt <= end) {
        arr.push(new Date(dt));
        dt.setMinutes(dt.getMinutes() + 30);
        dt.setSeconds(0);
      }
      arr.push(end);
      return arr.map(el => el.getTime() / 1000);
    }
  }
};
</script>

<style scoped>
.md-toolbar {
  position: inherit;
}

.chart-column {
  height: 300px;
}
</style>